import React from "react";
import { completeTabuProcedure } from "@10x/tabu-mobile-api/dist/requests/api";
import { CompleteTabuProcedure, TabuCertificate, TabuRequestStatusType } from "@10x/tabu-mobile-api/dist/requests/entity";
import { TabuUtils } from "@10x/tabu-mobile-api";
import Strings from "@my-dld/assets/strings";
import { CurrencyUtil, DateUtil, LocaleUtil, StringUtil } from "@eres/core";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { SitePlan } from "@my-dld/utils/TabuServices";
import BaseTabuRequestForm, { BaseTabuRequestProps } from "./BaseTabuRequestForm";
import { MyRequestsBCItem } from "@my-dld/contexts/PageContext";
import PageTitle from "@my-dld/components/PageTitle";
import { Redirect } from "react-router";
import PageContent from "@my-dld/components/PageContent";
import { sanitizeTabuMobileDocumentUrlNext } from "@my-dld/utils/UrlUtils";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import { formatAppDate } from "@my-dld/utils/Utils";

export interface CompleteTabuRequestData {
	ProcedureId: string;
	ProcedureNumber: string;
	ProcedureYear: string;
	PaymentRef: string;
	RequestStatusId: number;
}

interface State {
	procedure?: CompleteTabuProcedure;
	isLoading: boolean;
}

class CompleteTabuRequest extends BaseTabuRequestForm<BaseTabuRequestProps, State> {
	constructor( props: BaseTabuRequestProps ) {
		super( props );
		this.state = {
			isLoading: true,
		};
	}

	componentDidMount() {
		this.completeProcedure();
	}

	render() {
		const { procedure } = this.state;
		let voucherNo, requestNo, dateTime, amount;
		const data: CompleteTabuRequestData = this.props.location.state;
		if ( data == null ) return <Redirect to={ "/mydld" }/>;
		let message = Strings.request_submitted_successfully;
		if ( procedure ) {
			if ( procedure.ReceiptList && procedure.ReceiptList.length > 0 ) {
				const voucher = procedure.ReceiptList[ 0 ];
				const period = DateUtil.parseDate( voucher.ReceiptDate, "YYYY-MM-DDThh:mm:ss" );
				const date = formatAppDate( period, "en" );
				const time = DateUtil.formatDate( period, "hh:mm a", "en" );
				dateTime = `${ date } ${ time }`;
				amount = `${ CurrencyUtil.convertValueToCurrency( voucher.Amount ) } ${ Strings.aed }`;
				voucherNo = `${ voucher.VoucherNumber }/${ voucher.VoucherYear }`;
			}
			requestNo = `${ procedure.ProcedureNumber }-${ procedure.ProcedureId }-${ procedure.ProcedureYear }`;
		} else {
			requestNo = `${ data.ProcedureNumber }-${ data.ProcedureId }-${ data.ProcedureYear }`;
			message = Strings.request_submitted_successfully_under_audit;
		}
		return <PageContent>
			<PageTitle title={ undefined } paths={ [ MyRequestsBCItem ] }/>
			{ ( procedure || data.RequestStatusId === TabuRequestStatusType.UNDER_AUDIT ) && <React.Fragment>
				<div
					id="success"
					className="col-xl-12 col-md-12 col-sm-12 col-12 success-check-icon">
					<div className="icon icon--order-success svg">
						<div className="svg-check-icon"/>
					</div>
					<h5 className="mt-4">{ message }</h5>
				</div>
				<div className="card request-complete">
					<div className="row">
						<div className="col-xl-12 col-md-12 col-sm-12 col-12">
							<div className="card-body p-0">
								<ul className="list-group list-group-flush">
									{ !StringUtil.isEmptyStringOrNull( voucherNo ) &&
										<li className="list-group-item">
											<span>{ Strings.voucher_number }</span>
											<span>{ voucherNo }</span>
										</li>
									}
									<li className="list-group-item">
										<span>{ Strings.request_number }</span>
										<span>{ requestNo }</span>
									</li>
									{ !StringUtil.isEmptyStringOrNull( dateTime ) &&
										<li className="list-group-item">
											<span>{ Strings.date_and_time }</span>
											<span>{ dateTime }</span>
										</li>
									}
									{ !StringUtil.isEmptyStringOrNull( amount ) &&
										<li className="list-group-item">
											<span>{ Strings.amount }</span>
											<span>{ amount }</span>
										</li>
									}
									{ procedure && procedure.Certificate && procedure.Certificate.length > 0 &&
										<RenderDocuments
											docs={ procedure.Certificate }
											onClick={ this.onDocClick }/>
									}
									{ procedure && !StringUtil.isEmptyStringOrNull( procedure.AffectionPlanURL ) &&
										<RenderSitePlan
											link={ procedure.AffectionPlanURL }
											onClick={ this.onLinkClick }/>
									}
								</ul>
							</div>
						</div>
					</div>
				</div>
			</React.Fragment> }
			{ procedure == undefined && !( data.RequestStatusId === TabuRequestStatusType.UNDER_AUDIT || data.RequestStatusId === TabuRequestStatusType.REJECTED ) &&
				<React.Fragment>
					<div
						id="success"
						className="col-xl-12 col-md-12 col-sm-12 col-12 success-check-icon">
						<div className="icon">
							<i className="fa fa-2x fa-hourglass-half"/>
						</div>
						<h5 className="mt-4">{ Strings.transaction_processing }</h5>
					</div>
				</React.Fragment> }
		</PageContent>;
	}

	private completeProcedure() {
		const data: CompleteTabuRequestData = this.props.location.state;
		if ( data == null ) return;
		if ( data.RequestStatusId === TabuRequestStatusType.UNDER_AUDIT || data.RequestStatusId === TabuRequestStatusType.REJECTED ) {
			this.setState( { isLoading: false, } );
			return;
		}
		showLoading();
		trackEventWithData( "TabuProcedure", "CompleteProcedureTry", data );
		completeTabuProcedure( data.ProcedureId, data.ProcedureYear, data.ProcedureNumber, data.PaymentRef )
		.then( TabuUtils.validateResponse ).then( ( resp: CompleteTabuProcedure ) => {
			hideLoading();
			this.setState( { procedure: resp } );
			trackEventWithData( "TabuProcedure", "CompleteProcedureSuccess", data );
		} ).catch( ( reason ) => {
			hideLoading();
			showErrorMessage( reason.toString() );
			trackEventWithData( "TabuProcedure", "CompleteProcedureFail", { reason: reason.toString(), ...data } );
		} );
	}

	private onDocClick = ( doc: TabuCertificate ) => {
		let url = doc.FileName;
		sanitizeTabuMobileDocumentUrlNext( url );
	};

	private onLinkClick = ( link: string ) => {
		window.open( link );
	};
}

function RenderSitePlan( props: { link: string, onClick: ( link: string ) => void } ) {
	const { link, onClick } = props;
	return ( <li className="list-group-item">
		<span>{ Strings.site_plan_browser }</span>
		<span>
            <button
	            className="btn-link btn"
	            onClick={ () => onClick( link ) }>{ LocaleUtil.getLocalValue( SitePlan.Name ) }
	            <i className={ "fa fa-globe ms-3" }/></button>
        </span>
	</li> );
}

function RenderDocuments( props: { docs: TabuCertificate[], onClick: ( doc: TabuCertificate ) => void } ) {
	const { docs, onClick } = props;
	return ( <li className="list-group-item">
		<span>{ Strings.download_certificate }</span>
		<span>
            { docs.map( ( doc: TabuCertificate, index: number ) => {
	            return <button
		            className="btn-link btn" key={ index }
		            onClick={ () => onClick( doc ) }>{ doc.FileDisplayName }<i
		            className={ "fa fa-download ms-3" } key={ doc.FileDisplayName }/>
	            </button>;
            } ) }
        </span>
	</li> );
}

export default CompleteTabuRequest;
